"use client";

import { useState } from "react";

export interface FilterState {
  vacancyOnly: boolean;
  builtWithinYears: number | null;
  facilities: string[];
  walkWithinMinutes: number | null;
}

export const INITIAL_FILTERS: FilterState = {
  vacancyOnly: false,
  builtWithinYears: null,
  facilities: [],
  walkWithinMinutes: null,
};

const FACILITY_OPTIONS = [
  "エレベーター",
  "エアコン",
  "オートドアロック",
  "追い焚き",
  "光配線",
  "ディスポーザー",
  "フローリング",
  "宅配ボックス",
  "洗面所独立",
  "バルコニー",
];

const BUILT_YEAR_OPTIONS = [
  { label: "5年以内", value: 5 },
  { label: "10年以内", value: 10 },
  { label: "15年以内", value: 15 },
  { label: "20年以内", value: 20 },
  { label: "30年以内", value: 30 },
  { label: "40年以内", value: 40 },
];

const WALK_OPTIONS = [
  { label: "5分以内", value: 5 },
  { label: "10分以内", value: 10 },
  { label: "15分以内", value: 15 },
  { label: "20分以内", value: 20 },
];

interface Props {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
}

function CheckboxGroup({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
}) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const checked = selected.includes(opt);
          return (
            <label
              key={opt}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs cursor-pointer transition-colors ${
                checked
                  ? "bg-accent/10 border-accent text-accent"
                  : "border-border text-muted hover:border-gray-400"
              }`}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={checked}
                onChange={() => {
                  onChange(
                    checked
                      ? selected.filter((s) => s !== opt)
                      : [...selected, opt]
                  );
                }}
              />
              {opt}
            </label>
          );
        })}
      </div>
    </div>
  );
}

export function FilterPanel({ filters, onChange }: Props) {
  const [open, setOpen] = useState(false);

  const activeCount =
    (filters.vacancyOnly ? 1 : 0) +
    (filters.builtWithinYears ? 1 : 0) +
    filters.facilities.length +
    (filters.walkWithinMinutes ? 1 : 0);

  return (
    <div className="mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm text-muted hover:text-foreground transition-colors"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
          />
        </svg>
        フィルター
        {activeCount > 0 && (
          <span className="bg-accent text-white text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">
            {activeCount}
          </span>
        )}
        <svg
          className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-border space-y-4">
          {/* 空室のみ */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.vacancyOnly}
              onChange={(e) =>
                onChange({ ...filters, vacancyOnly: e.target.checked })
              }
              className="rounded border-gray-300 text-accent focus:ring-accent"
            />
            <span className="text-sm">空室のある物件のみ表示</span>
          </label>

          {/* 建築年・徒歩分数 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-muted mb-2">建築年</p>
              <select
                value={filters.builtWithinYears ?? ""}
                onChange={(e) =>
                  onChange({
                    ...filters,
                    builtWithinYears: e.target.value ? Number(e.target.value) : null,
                  })
                }
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-accent"
              >
                <option value="">指定なし</option>
                {BUILT_YEAR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted mb-2">駅からの徒歩</p>
              <select
                value={filters.walkWithinMinutes ?? ""}
                onChange={(e) =>
                  onChange({
                    ...filters,
                    walkWithinMinutes: e.target.value ? Number(e.target.value) : null,
                  })
                }
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-accent"
              >
                <option value="">指定なし</option>
                {WALK_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 設備 */}
          <CheckboxGroup
            label="設備"
            options={FACILITY_OPTIONS}
            selected={filters.facilities}
            onChange={(facilities) => onChange({ ...filters, facilities })}
          />

          {/* リセットボタン */}
          {activeCount > 0 && (
            <button
              onClick={() => onChange(INITIAL_FILTERS)}
              className="text-xs text-muted hover:text-accent transition-colors"
            >
              フィルターをリセット
            </button>
          )}
        </div>
      )}
    </div>
  );
}
